/**
 * Agent Prompt Preview Component
 * Shows the rendered system prompt for an agent configuration using real data
 */

import { useState, useMemo, useEffect } from 'react';
import { Eye, RefreshCw, Copy, Check, ChevronDown, ChevronRight, User, AlertCircle, MessageSquare, Zap, Target, Layers, Wrench } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { promptTemplatesApi } from '@/lib/api/promptTemplates';
import { usePromptTemplates } from '@/features/prompt-templates';
import { useAuthStore } from '@/stores/authStore';
import { useI18n } from '@/i18n';
import type { LivePreviewResponse } from '@/types/promptTemplates';

interface AgentPromptPreviewProps {
  templateId: number | null;
  agentType: string;
}

export function AgentPromptPreview({ templateId, agentType }: AgentPromptPreviewProps) {
  const { user } = useAuthStore();
  const currentUserId = user?.user_id || '';
  const { messages } = useI18n();
  const t = messages.agentConfig.promptPreview;

  const [preview, setPreview] = useState<LivePreviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showDataSources, setShowDataSources] = useState(false);
  const [showHooksInfo, setShowHooksInfo] = useState(false);
  const [showUserMessage, setShowUserMessage] = useState(false);
  const [showClassifier, setShowClassifier] = useState(true);
  const [userId, setUserId] = useState<string>('');
  const [userMessage, setUserMessage] = useState<string>('你好，我想了解一下我的运势');
  const [useRealData, setUseRealData] = useState(true);

  // Initialize userId with current user when available
  useEffect(() => {
    if (currentUserId && !userId) {
      setUserId(currentUserId);
    }
  }, [currentUserId, userId]);

  const { data: templates = [] } = usePromptTemplates(true);

  // Get selected template info
  const selectedTemplate = useMemo(() => {
    if (templateId === null) {
      return templates.find(t => t.is_default);
    }
    return templates.find(t => t.id === templateId);
  }, [templateId, templates]);

  // Load preview using live preview API
  const handlePreview = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await promptTemplatesApi.livePreview({
        agent_type: agentType,
        user_id: useRealData && userId.trim() ? userId.trim() : undefined,
        user_message: userMessage.trim() || undefined,
      });
      setPreview(result);
    } catch (err) {
      setError((err as Error).message || 'Failed to load preview');
    } finally {
      setIsLoading(false);
    }
  };

  // Copy to clipboard
  const handleCopy = async (content: string) => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Get badge color for data source
  const getSourceBadgeColor = (source: string) => {
    switch (source) {
      case 'real':
        return 'bg-emerald-100 text-emerald-700';
      case 'sample':
        return 'bg-blue-100 text-blue-700';
      case 'empty':
        return 'bg-yellow-100 text-yellow-700';
      case 'error':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-slate-900">{t?.title}</h3>
        </div>
        <div className="flex items-center gap-2">
          {preview && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleCopy(preview.system_prompt)}
            >
              {copied ? (
                <Check className="w-4 h-4 mr-2 text-emerald-600" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              {copied ? t?.copied : t?.copy}
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={handlePreview}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? t?.loading : preview ? t?.refresh : t?.generatePreview}
          </Button>
        </div>
      </div>

      {/* Template Info */}
      {selectedTemplate && (
        <div className="mb-4 p-3 bg-slate-50 rounded-lg">
          <p className="text-sm text-slate-600">
            {t?.usingTemplate}: <span className="font-medium text-slate-900">{selectedTemplate.name}</span>
            {templateId === null && (
              <span className="ml-2 text-xs text-primary-600">({t?.default})</span>
            )}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {t?.enabledSections?.replace('{count}', String(selectedTemplate.sections.filter(s => s.enabled).length))}
          </p>
        </div>
      )}

      {/* Live Preview Options */}
      <div className="mb-4 p-4 border border-slate-200 rounded-lg space-y-4">
        {/* Use Real Data Toggle */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="useRealData"
            checked={useRealData}
            onChange={(e) => setUseRealData(e.target.checked)}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label htmlFor="useRealData" className="text-sm font-medium text-slate-700">
            {t?.useRealData}
          </label>
          <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded">
            {t?.livePreview}
          </span>
        </div>

        {useRealData && (
          <div className="space-y-3 pl-7">
            {/* User ID Input */}
            <div className="space-y-1">
              <label className="text-xs text-slate-500">{t?.userId}</label>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder={t?.enterUserId}
                  className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                {currentUserId && userId !== currentUserId && (
                  <button
                    type="button"
                    onClick={() => setUserId(currentUserId)}
                    className="px-3 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
                  >
                    {t?.useCurrent}
                  </button>
                )}
              </div>
              {userId === currentUserId && currentUserId && (
                <p className="text-xs text-emerald-600 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  {t?.usingCurrentUser}
                </p>
              )}
            </div>

            {/* User Message Input */}
            <div className="space-y-1">
              <label className="text-xs text-slate-500">{t?.sampleMessage}</label>
              <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 text-slate-400 flex-shrink-0 mt-2" />
                <textarea
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  placeholder={t?.enterSampleMessage}
                  rows={2}
                  className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                />
              </div>
            </div>
          </div>
        )}

        <p className="text-xs text-slate-500 flex items-center gap-1">
          <Zap className="w-3 h-3" />
          {useRealData ? t?.realDataHint : t?.sampleDataHint}
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-600 text-sm font-medium">{t?.previewFailed}</p>
            <p className="text-red-500 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Preview Result */}
      {preview ? (
        <div className="space-y-4">
          {/* Stats */}
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
            {preview.template_name && (
              <span>
                {t?.template} <span className="font-medium text-slate-700">{preview.template_name}</span>
              </span>
            )}
            <span className="text-slate-300">|</span>
            <span>
              {t?.systemChars?.replace('{count}', preview.total_system_prompt_length.toLocaleString())}
            </span>
            <span className="text-slate-300">|</span>
            <span>
              {t?.userChars?.replace('{count}', preview.total_user_message_length.toLocaleString())}
            </span>
            <span className="text-slate-300">|</span>
            <span className="text-emerald-600">
              {t?.user} {preview.user_id.slice(0, 16)}...
            </span>
          </div>

          {/* Hooks Executed Accordion */}
          <button
            type="button"
            onClick={() => setShowHooksInfo(!showHooksInfo)}
            className="w-full flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            {showHooksInfo ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            {t?.hooksExecuted?.replace('{count}', String(preview.hooks_executed.system_stage.length + preview.hooks_executed.user_stage.length))}
          </button>

          {showHooksInfo && (
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">{t?.systemStage}</p>
                <div className="flex flex-wrap gap-2">
                  {preview.hooks_executed.system_stage.length > 0 ? (
                    preview.hooks_executed.system_stage.map((hook) => (
                      <span key={hook} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                        {hook}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-slate-400">{t?.noHooksExecuted}</span>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">{t?.userStage}</p>
                <div className="flex flex-wrap gap-2">
                  {preview.hooks_executed.user_stage.length > 0 ? (
                    preview.hooks_executed.user_stage.map((hook) => (
                      <span key={hook} className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                        {hook}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-slate-400">{t?.noHooksExecuted}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Classifier Debug Info */}
          {preview.classifier && (
            <>
              <button
                type="button"
                onClick={() => setShowClassifier(!showClassifier)}
                className="w-full flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
              >
                {showClassifier ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                <Target className="w-4 h-4 text-orange-500" />
                <span>
                  Classifier 执行详情
                  {preview.classifier.matched_skills.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                      匹配 {preview.classifier.matched_skills.length} 个技能
                    </span>
                  )}
                </span>
              </button>

              {showClassifier && (
                <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg space-y-4 border border-orange-100">
                  {/* Classification Result */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-2 bg-white rounded border border-orange-100">
                      <p className="text-xs text-slate-500">Response Type</p>
                      <p className="text-sm font-medium text-slate-900">
                        {preview.classifier.response_type || 'N/A'}
                      </p>
                    </div>
                    <div className="p-2 bg-white rounded border border-orange-100">
                      <p className="text-xs text-slate-500">Confidence</p>
                      <p className="text-sm font-medium text-slate-900">
                        {preview.classifier.confidence != null
                          ? `${(preview.classifier.confidence * 100).toFixed(0)}%`
                          : 'N/A'}
                      </p>
                    </div>
                    <div className="p-2 bg-white rounded border border-orange-100">
                      <p className="text-xs text-slate-500">Match Layer</p>
                      <p className="text-sm font-medium text-slate-900">
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          preview.classifier.match_layer === 'rule' ? 'bg-green-100 text-green-700' :
                          preview.classifier.match_layer === 'semantic' ? 'bg-blue-100 text-blue-700' :
                          preview.classifier.match_layer === 'llm' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {preview.classifier.match_layer || 'N/A'}
                        </span>
                      </p>
                    </div>
                    <div className="p-2 bg-white rounded border border-orange-100">
                      <p className="text-xs text-slate-500">Skill Tools</p>
                      <p className="text-sm font-medium">
                        {preview.classifier.skill_tools_registered ? (
                          <span className="flex items-center gap-1 text-emerald-600">
                            <Wrench className="w-3 h-3" />
                            已注册
                          </span>
                        ) : (
                          <span className="text-slate-400">未注册</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Matched Skills */}
                  {preview.classifier.matched_skills.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
                        <Layers className="w-3 h-3" />
                        匹配的技能
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {preview.classifier.matched_skills.map((skill, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1.5 bg-orange-100 text-orange-800 rounded-lg text-sm border border-orange-200"
                            title={`Confidence: ${skill.confidence || 'N/A'}, Layer: ${skill.match_layer || 'N/A'}`}
                          >
                            <span className="font-medium">{skill.skill_id}</span>
                            {skill.confidence != null && (
                              <span className="ml-2 text-xs opacity-70">
                                {(skill.confidence * 100).toFixed(0)}%
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Available Skills */}
                  {preview.classifier.available_skills.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-2">可用技能</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {preview.classifier.available_skills.map((skill) => (
                          <div
                            key={skill.skill_id}
                            className={`p-2 rounded border ${
                              preview.classifier?.matched_skills.some(m => m.skill_id === skill.skill_id)
                                ? 'bg-orange-50 border-orange-200'
                                : 'bg-white border-slate-100'
                            }`}
                          >
                            <p className="text-sm font-medium text-slate-800">{skill.name}</p>
                            <p className="text-xs text-slate-500">{skill.skill_id}</p>
                            {skill.description && (
                              <p className="text-xs text-slate-400 mt-1 truncate">{skill.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Injected Instructions Preview */}
                  {preview.classifier.injected_instructions && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-2">注入的技能指令</p>
                      <pre className="p-3 bg-white rounded border border-orange-100 text-xs text-slate-700 overflow-auto max-h-32 whitespace-pre-wrap">
                        {preview.classifier.injected_instructions.slice(0, 500)}
                        {preview.classifier.injected_instructions.length > 500 && '...'}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Data Sources Accordion */}
          <button
            type="button"
            onClick={() => setShowDataSources(!showDataSources)}
            className="w-full flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            {showDataSources ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            {t?.dataSources?.replace('{count}', String(Object.keys(preview.data_sources).length))}
          </button>

          {showDataSources && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(preview.data_sources).map(([key, info]) => (
                  <div key={key} className="flex items-center justify-between p-2 bg-white rounded border border-slate-100">
                    <span className="text-sm text-slate-600">{key}</span>
                    <div className="flex items-center gap-2">
                      {typeof info === 'object' && info.length && (
                        <span className="text-xs text-slate-400">{info.length} chars</span>
                      )}
                      <span className={`px-2 py-0.5 text-xs rounded ${getSourceBadgeColor(typeof info === 'object' ? info.source : info)}`}>
                        {typeof info === 'object' ? info.source : info}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">
                {t?.dataSourceHint}
              </p>
            </div>
          )}

          {/* System Prompt */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-slate-700">{t?.systemPrompt}</h4>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleCopy(preview.system_prompt)}
              >
                {copied ? (
                  <Check className="w-4 h-4 mr-1 text-emerald-600" />
                ) : (
                  <Copy className="w-4 h-4 mr-1" />
                )}
                {t?.copy}
              </Button>
            </div>
            <div className="relative">
              <pre className="p-4 bg-slate-900 text-slate-100 rounded-lg overflow-auto max-h-[400px] text-sm whitespace-pre-wrap font-mono">
                {preview.system_prompt}
              </pre>
            </div>
          </div>

          {/* User Message Toggle */}
          <button
            type="button"
            onClick={() => setShowUserMessage(!showUserMessage)}
            className="w-full flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            {showUserMessage ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            {t?.enhancedUserMessage?.replace('{count}', String(preview.total_user_message_length))}
          </button>

          {showUserMessage && (
            <div className="relative">
              <pre className="p-4 bg-slate-800 text-slate-100 rounded-lg overflow-auto max-h-[200px] text-sm whitespace-pre-wrap font-mono">
                {preview.user_message}
              </pre>
            </div>
          )}

          {/* Context Items */}
          {preview.context_items.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">{t?.contextItems?.replace('{count}', String(preview.context_items.length))}</h4>
              <div className="flex flex-wrap gap-2">
                {preview.context_items.map((item, idx) => (
                  <span
                    key={idx}
                    className={`px-2 py-1 text-xs rounded ${
                      item.scope === 'system_prompt' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}
                    title={`${item.scope} | order: ${item.order} | ${item.content_length} chars`}
                  >
                    {item.name}
                    <span className="ml-1 opacity-60">({item.content_length})</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="p-8 border-2 border-dashed border-slate-200 rounded-lg text-center">
          <Eye className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">
            {t?.emptyStateTitle}
          </p>
          <p className="text-sm text-slate-400 mt-2">
            {t?.emptyStateDesc}
          </p>
        </div>
      )}
    </Card>
  );
}
